/**
 * DeleteProjectDialog — confirmación destructiva para borrar un proyecto.
 *
 * Patrones:
 *  - Modal centrado con `Dialog` (sin `confirm()` ni `alert()`).
 *  - Texto claro: la acción es IRREVERSIBLE, sin backup ni recovery.
 *  - Lista de qué se borra (phases, members, artifacts, snapshot) +
 *    TODOs marcados para integraciones futuras (Blob, Qdrant, Neo4j).
 *  - Type-to-confirm: el usuario debe escribir el nombre EXACTO del
 *    proyecto. El botón "Borrar permanentemente" queda deshabilitado
 *    hasta que el match sea exacto (case-sensitive, trim de whitespace).
 *  - Mientras la mutation corre el modal queda con todo deshabilitado
 *    para evitar doble-submit.
 *
 * Feedback post-mutation:
 *  - Success → toast `success` + caller cierra modal y refresca lista.
 *  - Error → toast `critical` con el code traducido del backend; el
 *    modal queda abierto para reintentar.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'

import { ApiError } from '../../api/client'
import { deleteProject, type Project } from '../../api/projects'
import { useAccessToken } from '../../auth/useAccessToken'
import { Dialog } from '../Dialog'
import { pushToast } from '../../stores/toastStore'

interface DeleteProjectDialogProps {
  project: Project | null
  open: boolean
  onClose: () => void
  /** Llamado tras un DELETE exitoso, antes de cerrar el modal. */
  onDeleted?: (projectId: string) => void
}

export function DeleteProjectDialog({
  project,
  open,
  onClose,
  onDeleted,
}: DeleteProjectDialogProps) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()
  const [confirmText, setConfirmText] = useState('')

  const mutation = useMutation({
    mutationFn: async () => {
      if (!accessToken || !project) throw new Error('no_context')
      await deleteProject(accessToken, project.id)
    },
    onSuccess: () => {
      if (!project) return
      // Invalidar cualquier query relacionada con la lista o este proyecto.
      void queryClient.invalidateQueries({ queryKey: ['projects', 'list'] })
      void queryClient.invalidateQueries({
        queryKey: ['projects', project.id],
      })
      pushToast({
        kind: 'success',
        message: t('delete.toast_success', { name: project.name }),
      })
      onDeleted?.(project.id)
      setConfirmText('')
      onClose()
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : null
      const fallback = t('delete.toast_failed')
      const message = code
        ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
        : fallback
      pushToast({ kind: 'critical', message, duration: 6000 })
    },
  })

  if (!project) return null

  const isMatch = confirmText.trim() === project.name
  const submitting = mutation.isPending

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (submitting) return
        setConfirmText('')
        onClose()
      }}
      title={t('delete.title')}
      footer={
        <>
          <button
            type="button"
            onClick={() => {
              setConfirmText('')
              onClose()
            }}
            disabled={submitting}
            className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
          >
            {t('delete.actions.cancel')}
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!isMatch || submitting}
            className="px-5 py-2 bg-critical hover:bg-critical/90 text-canvas text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting
              ? t('delete.actions.deleting')
              : t('delete.actions.delete')}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Warning bloque destacado */}
        <div className="rounded-md border border-critical/40 bg-critical-soft p-3 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-critical mt-0.5 shrink-0" />
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-critical">
              {t('delete.warning_eyebrow')}
            </p>
            <p className="mt-1 font-body text-sm text-ink">
              {t('delete.warning_lead', { name: project.name })}
            </p>
          </div>
        </div>

        {/* Lista de qué se borra */}
        <div>
          <p className="text-xs font-medium text-ink-muted mb-2">
            {t('delete.what_gets_deleted_title')}
          </p>
          <ul className="space-y-1.5 text-sm font-body text-ink-muted">
            <li className="flex gap-2">
              <span className="text-critical">·</span>
              {t('delete.items.phases')}
            </li>
            <li className="flex gap-2">
              <span className="text-critical">·</span>
              {t('delete.items.members')}
            </li>
            <li className="flex gap-2">
              <span className="text-critical">·</span>
              {t('delete.items.artifacts')}
            </li>
            <li className="flex gap-2">
              <span className="text-critical">·</span>
              {t('delete.items.snapshot')}
            </li>
            {/* TODO(B.7): Mover a items reales cuando Blob storage entre. */}
            <li className="flex gap-2 text-ink-subtle italic">
              <span>·</span>
              {t('delete.items.todo_blob')}{' '}
              <span className="font-mono text-[10px]">(B.7)</span>
            </li>
            {/* TODO(D): Mover a items reales cuando Qdrant entre. */}
            <li className="flex gap-2 text-ink-subtle italic">
              <span>·</span>
              {t('delete.items.todo_vector')}{' '}
              <span className="font-mono text-[10px]">(D)</span>
            </li>
            {/* TODO(D): Mover a items reales cuando Neo4j entre. */}
            <li className="flex gap-2 text-ink-subtle italic">
              <span>·</span>
              {t('delete.items.todo_graph')}{' '}
              <span className="font-mono text-[10px]">(D)</span>
            </li>
          </ul>
          <p className="mt-3 text-[11px] font-body text-ink-subtle italic">
            {t('delete.audit_note')}
          </p>
        </div>

        {/* Type-to-confirm */}
        <div>
          <label
            htmlFor="delete-project-confirm"
            className="block text-xs font-medium text-ink-muted mb-1.5"
          >
            {t('delete.confirm_label')}{' '}
            <span className="font-mono text-ink">«{project.name}»</span>
          </label>
          <input
            id="delete-project-confirm"
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={t('delete.confirm_placeholder', { name: project.name })}
            disabled={submitting}
            className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-critical font-mono"
          />
        </div>
      </div>
    </Dialog>
  )
}
